// Idle-based auto-logout.
//
// Most positions are logged out after 15 minutes of inactivity. Manager+
// (Manager, Admin — and their legacy admin/superAdmin aliases) get 1 day,
// since they often leave dashboards open across shifts. Activity is any
// user input (scan, keypress, click, scroll, touch) and is shared across
// tabs via localStorage — an active tab keeps idle tabs alive.

import { isManagerPlus, readUserPosition } from "../../access/positions";

const ACTIVITY_KEY = "lastActivity";
// Ceiling on how often we write to localStorage. Real activity bursts get
// collapsed so we're not writing on every mousemove or scan keystroke.
const BUMP_DEBOUNCE_MS = 30_000;
// How often each tab independently checks whether the shared timestamp has
// gone stale. Cheap (one localStorage read).
const CHECK_INTERVAL_MS = 30_000;

const MANAGER_PLUS_TIMEOUT_MS = 24 * 60 * 60 * 1000;
// Conservative default for unknown/missing/every-other position — match the
// basic timeout so misconfigured users err on the side of being logged out.
const FALLBACK_TIMEOUT_MS = 15 * 60 * 1000;

const isAuthed = (): boolean =>
    window.localStorage.getItem("isLoggedIn") === "true";

const getTimeoutMs = (): number => {
    const position = readUserPosition();
    if (position === null) return FALLBACK_TIMEOUT_MS;
    return isManagerPlus(position) ? MANAGER_PLUS_TIMEOUT_MS : FALLBACK_TIMEOUT_MS;
};

export const markActive = (): void => {
    window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
};

export const clearActivity = (): void => {
    window.localStorage.removeItem(ACTIVITY_KEY);
};

let lastBumpedAt = 0;
const onUserEvent = (): void => {
    if (!isAuthed()) return;
    const now = Date.now();
    if (now - lastBumpedAt < BUMP_DEBOUNCE_MS) return;
    lastBumpedAt = now;
    markActive();
};

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    "mousedown",
    "keydown",
    "touchstart",
    "scroll",
    "wheel",
];

export const installIdleLogout = (onTimeout: () => void): (() => void) => {
    // On mount: if the previous session is already stale, log out before
    // protected routes get a chance to render and fire authed fetches.
    if (isAuthed()) {
        const lastRaw = window.localStorage.getItem(ACTIVITY_KEY);
        const last = lastRaw ? parseInt(lastRaw, 10) : 0;
        if (last > 0 && Date.now() - last > getTimeoutMs()) {
            onTimeout();
            return () => { /* no listeners installed */ };
        }
        // Treat this mount as activity. Covers the just-logged-in case and
        // any user upgrade path where the key didn't exist yet.
        markActive();
        lastBumpedAt = Date.now();
    }

    ACTIVITY_EVENTS.forEach(e =>
        window.addEventListener(e, onUserEvent, { passive: true })
    );

    const interval = window.setInterval(() => {
        if (!isAuthed()) return;
        const lastRaw = window.localStorage.getItem(ACTIVITY_KEY);
        const last = lastRaw ? parseInt(lastRaw, 10) : 0;
        if (last <= 0) return;
        if (Date.now() - last > getTimeoutMs()) {
            onTimeout();
        }
    }, CHECK_INTERVAL_MS);

    return () => {
        ACTIVITY_EVENTS.forEach(e =>
            window.removeEventListener(e, onUserEvent)
        );
        window.clearInterval(interval);
    };
};
